import { IS_DEV_SERVER, IS_NODE } from "@/__main__/constants.mjs";
import type { DataSource } from "@/__main__/get-data.mjs";
import { devError } from "@/util/dev.mjs";

const shouldCheck = !IS_NODE && IS_DEV_SERVER;

const R1 = /fetchData|updateRoute|setRoute/m;
const R2 = /commitHook|commitPassive/m;

let isRouteSemaphore = false;

if (shouldCheck) {
  // To check async stack traces in runtime, we need to instrument
  // Promise globally. This has a performance penalty and should only
  // be run on dev.
  const originalThen = Promise.prototype.then;
  // eslint-disable-next-line no-extend-native
  Promise.prototype.then = function instrumentedThen(fn1, fn2) {
    const isFromRoute = R1.test(new Error().stack);

    function monkeyPatch(fn) {
      return function (arg) {
        if (isFromRoute) {
          // This makes sure that we have to be on the same tick.
          if (!isRouteSemaphore) {
            queueMicrotask(() => {
              isRouteSemaphore = false;
            });
          }
          isRouteSemaphore = true;
        }
        return fn(arg);
      };
    }

    const monkeyFn1 = typeof fn1 === "function" ? monkeyPatch(fn1) : fn1;
    const monkeyFn2 = typeof fn2 === "function" ? monkeyPatch(fn2) : fn2;

    return originalThen.call(this, monkeyFn1, monkeyFn2);
  };
}

export default function checkGetData(
  dataSource: DataSource,
  { root }: { root?: object },
): null {
  if (shouldCheck) {
    const e = new Error();
    const { stack } = e;

    // Sometimes we get extremely short stack traces, I'm not sure why.
    // Can only bail out in this case.
    if (stack.split("\n").length < 5) {
      // devDebug(
      //   `Skipped check for getData due to truncated stack. Path: "${writeStatePath.join(
      //     "."
      //   )}"`
      // );
      return null;
    }

    const isFromRoute = R1.test(stack) || root || isRouteSemaphore;

    if (R2.test(stack) && !isFromRoute) {
      const error = new Error(
        `A request was triggered from a React component, this is explicitly forbidden in all circumstances. ${dataSource}`,
      );
      devError("Developer Error, check your code!", error);
      throw error;
    }

    if (!isFromRoute) {
      const error = new Error(
        `A request was not called from fetchData, this is mandatory unless writeStatePath.root is specified. ${dataSource}`,
      );
      devError("Developer Error, check your code!", error);
      throw error;
    }
  }

  return null;
}
